import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Eye, X, Calendar, User, CreditCard, CheckCircle, XCircle, Clock, Plane, RefreshCw } from 'lucide-react';
import TripjackCancelModal from '../../components/TripjackCancelModal';
import RiyaCancelModal from '../../components/RiyaCancelModal';
import RiyaRescheduleModal from '../../components/RiyaRescheduleModal';
import RiyaSSRModal from '../../components/RiyaSSRModal';
import ConfirmHoldModal from '../../components/ConfirmHoldModal';
import PaymentAndRefundCard from '../../components/PaymentAndRefundCard';
import {
  getLegBaggageRowsForBooking,
  getNonBaggageSsrRowsForBooking,
} from '../../utils/ssrBreakdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: '',
    user_type: '',
    payment_method: '',
    from_date: '',
    to_date: '',
    search: ''
  });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [stats, setStats] = useState(null);
  // Sprint T1 — Tripjack cancel modal state
  const [tjCancelOpen, setTjCancelOpen] = useState(false);
  const [tjCancelBookingId, setTjCancelBookingId] = useState(null);
  const [tjResumePollingId, setTjResumePollingId] = useState(null);
  const [tjAdminAmendmentBanner, setTjAdminAmendmentBanner] = useState(null);
  // Sprint A — Riya cancel modal state
  const [riyaCancelOpen, setRiyaCancelOpen] = useState(false);
  const [riyaCancelMode, setRiyaCancelMode] = useState('cancel'); // 'cancel' | 'hold-cancel'
  const [riyaCancelBooking, setRiyaCancelBooking] = useState(null);
  // Sprint C — Riya reschedule + SSR modals
  const [riyaRescheduleBooking, setRiyaRescheduleBooking] = useState(null);
  const [riyaSSRBooking, setRiyaSSRBooking] = useState(null);
  // Sprint H1+H2 — Confirm Hold ticket modal state
  const [confirmHoldOpen, setConfirmHoldOpen] = useState(false);
  const [confirmHoldBooking, setConfirmHoldBooking] = useState(null);

  // Tripjack: show in-flight cancellation on admin booking detail + refetch after cancel modal closes
  useEffect(() => {
    if (!showDetailsModal || !selectedBooking) {
      setTjAdminAmendmentBanner(null);
      return;
    }
    const st = (selectedBooking.status || '').toLowerCase();
    if (st === 'cancelled') {
      setTjAdminAmendmentBanner(null);
      return;
    }
    const src = (selectedBooking.source || selectedBooking.flight_data?.source || '').toLowerCase();
    const hasTj = src === 'tripjack' || !!(selectedBooking.tripjack_booking_id || selectedBooking.supplier_booking_id);
    const bid = selectedBooking.booking_id || selectedBooking.id;
    if (!hasTj || !bid) {
      setTjAdminAmendmentBanner(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API}/tripjack/amendment/by-booking/${encodeURIComponent(bid)}`,
          { credentials: 'include' },
        );
        if (!r.ok || cancelled) return;
        const j = await r.json();
        const open = (j.amendments || []).find((a) =>
          ['REQUESTED', 'PENDING'].includes((a.amendment_status || '').toUpperCase()),
        );
        if (cancelled) return;
        setTjAdminAmendmentBanner(
          open?.amendment_id
            ? { amendment_id: open.amendment_id, amendment_status: open.amendment_status || '' }
            : null,
        );
      } catch {
        if (!cancelled) setTjAdminAmendmentBanner(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDetailsModal, selectedBooking, tjCancelOpen]);

  

  // Fetch bookings
  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await fetch(`${API}/admin/bookings?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch bookings');

      const data = await response.json();
      setBookings(data.bookings || []);
      setPagination(data.pagination || {});
    } catch (error) {
      console.error('Error fetching bookings:', error);
      alert('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/bookings/stats/summary`, {
        credentials: 'include',
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, [filters.page, filters.status, filters.user_type, filters.payment_method]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle search
  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
    fetchBookings();
  };

  // Handle download ticket
  const handleDownloadTicket = async (bookingId) => {
    try {
      const response = await fetch(`${API}/bookings/download-eticket/${bookingId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to download ticket');

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eticket_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading ticket:', error);
      alert('Failed to download e-ticket');
    }
  };


  // View booking details
  const viewBookingDetails = async (bookingId) => {
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}`, {
        credentials: 'include',
      });
      const data = await response.json();
      setSelectedBooking(data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      alert('Failed to load booking details');
    }
  };

  // Sprint T2 — Release a Tripjack Hold PNR
  const releaseHoldPnr = async (booking) => {
    if (!confirm('Release this Hold PNR? The seats will be returned to inventory and the booking marked UNCONFIRMED.')) return;
    try {
      const tjId = booking.tripjack_booking_id || booking.supplier_booking_id;
      const resp = await fetch(`${API}/tripjack/release-pnr`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId: tjId }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Failed to release hold');
      alert(data.message || 'Hold released successfully');
      fetchBookings();
      setShowDetailsModal(false);
    } catch (e) {
      alert(`Could not release hold: ${e.message}`);
    }
  };

  // Sprint P5 — Manual Tripjack refresh (booking-details → stamp local status/PNR/tickets)
  const refreshTripjackBooking = async (booking) => {
    try {
      const bookingId = booking?.booking_id || booking?.id;
      if (!bookingId) throw new Error('Missing booking id');
      const resp = await fetch(`${API}/admin/bookings/${bookingId}/tripjack/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Refresh failed');
      alert('Refreshed from Tripjack successfully');
      // Update list + modal
      fetchBookings();
      if (data?.booking) setSelectedBooking(data.booking);
    } catch (e) {
      alert(`Could not refresh: ${e.message}`);
    }
  };

  // Cancel booking — Sprint T1: route Tripjack bookings through the
  // amendment suite (real airline cancellation + refund); legacy internal
  // cancel kept as fallback for offline / non-Tripjack records.
  const cancelBooking = async (bookingId) => {
    const source = (selectedBooking?.source || selectedBooking?.flight_data?.source || '').toLowerCase();
    const hasTripjackId = !!(selectedBooking?.tripjack_booking_id);
    const hasRiyaId = !!(selectedBooking?.riya_pnr || source === 'riya');
    // Riya path — Sprint A
    if (source === 'riya' || hasRiyaId) {
      const status = (selectedBooking?.riya_status || selectedBooking?.status || '').toLowerCase();
      const isHeld = ['on_hold', 'hold', 'pending'].includes(status);
      setRiyaCancelMode(isHeld ? 'hold-cancel' : 'cancel');
      setRiyaCancelBooking(selectedBooking);
      setRiyaCancelOpen(true);
      return;
    }
    if (source === 'tripjack' || hasTripjackId) {
      setTjResumePollingId(null);
      setTjCancelBookingId(bookingId);
      setTjCancelOpen(true);
      return;
    }
    // Legacy fallback (offline / unknown source) — internal-only cancel
    const reason = prompt('Enter cancellation reason:');
    if (!reason) return;

    try {
      const response = await fetch(
        `${API}/admin/bookings/${bookingId}/cancel?reason=${encodeURIComponent(reason)}`,
        { method: 'PUT', credentials: 'include' },
      );

      if (!response.ok) throw new Error('Failed to cancel booking');

      const data = await response.json();
      alert(data.message + (data.refund_processed ? ` | Refund: ₹${data.refund_amount}` : ''));
      fetchBookings();
      setShowDetailsModal(false);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Failed to cancel booking');
    }
  };

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.user_type) params.append('user_type', filters.user_type);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);

      const response = await fetch(`${API}/admin/bookings/export/csv?${params}`, {
        credentials: 'include',
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (error) {
      console.error('Error exporting bookings:', error);
      alert('Failed to export bookings');
    }
  };

  // Status badge
  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded border ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Bookings Management</h1>
        <p className="text-gray-600">View and manage all flight bookings</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_bookings}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Confirmed</p>
                <p className="text-2xl font-bold text-green-600">{stats.confirmed_bookings}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending_bookings}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-purple-600">₹{stats.total_revenue?.toLocaleString()}</p>
              </div>
              <CreditCard className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Booking ID, PNR, Name..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* User Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
            <select
              value={filters.user_type}
              onChange={(e) => handleFilterChange('user_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="b2c">B2C</option>
              <option value="agent">B2B (Agent)</option>
            </select>
          </div>

          {/* Payment Method Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment</label>
            <select
              value={filters.payment_method}
              onChange={(e) => handleFilterChange('payment_method', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Methods</option>
              <option value="wallet">Wallet</option>
              <option value="gateway">Gateway</option>
            </select>
          </div>

          {/* Export Button */}
          <div className="flex items-end">
            <button
              onClick={exportToCSV}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters(prev => ({ ...prev, from_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters(prev => ({ ...prev, to_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">System Booking ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier Booking ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PNR</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travel Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time to Travel</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Passengers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="15" className="px-6 py-8 text-center text-gray-500">
                    Loading bookings...
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td colSpan="15" className="px-6 py-8 text-center text-gray-500">
                    No bookings found
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => {
                  const bookingId = booking.id || booking.booking_id;
                  const travelDateRaw = booking.travel_date || booking.flight_date || booking.flight_data?.flightDate || booking.flight_data?.segments?.[0]?.dt;
                  const travelDate = travelDateRaw ? new Date(travelDateRaw) : null;
                  const travelDateDisplay = travelDate && !isNaN(travelDate) ? travelDate.toLocaleDateString() : '-';
                  const source = booking.source || (booking.flight_data?.source) || 'offline';

                  // Days to travel
                  let timeToTravel = '-';
                  let timeBadgeClass = 'bg-gray-100 text-gray-600';
                  if (travelDate && !isNaN(travelDate)) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const td = new Date(travelDate);
                    td.setHours(0, 0, 0, 0);
                    const diffDays = Math.round((td - today) / (1000 * 60 * 60 * 24));
                    if (diffDays > 0) {
                      timeToTravel = `${diffDays} day${diffDays === 1 ? '' : 's'}`;
                      timeBadgeClass = diffDays <= 3 ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
                    } else if (diffDays === 0) {
                      timeToTravel = 'Today';
                      timeBadgeClass = 'bg-orange-100 text-orange-700';
                    } else {
                      timeToTravel = `${Math.abs(diffDays)}d ago`;
                      timeBadgeClass = 'bg-gray-100 text-gray-600';
                    }
                  }
                  
                  return (
                  <tr key={bookingId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-testid={`row-booking-id-${booking.booking_id}`}>
                      {booking.booking_id || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 font-mono" data-testid={`row-supplier-id-${booking.booking_id}`}>
                      {booking.supplier_booking_id || booking.tripjack_booking_id || booking.riya_pnr || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {booking.pnr || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {booking.route || '-'}
                      {booking.booking_type && booking.booking_type !== '-' && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                          {booking.booking_type}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {travelDateDisplay}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${timeBadgeClass}`}>
                        {timeToTravel}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        source === 'tripjack' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {source.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {booking.total_passengers || 1} pax
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{booking.user_details?.name || booking.user_details?.agency_name || booking.user_details?.full_name || '-'}</div>
                      <div className="text-xs text-gray-500">{booking.user_details?.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        booking.user_type === 'b2c' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {booking.user_type === 'b2c' ? 'B2C' : 'B2B'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">₹{booking.total_amount?.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        booking.payment_method === 'wallet' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {booking.payment_method}
                      </span>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(booking.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(booking.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownloadTicket(bookingId)}
                          className="text-green-600 hover:text-green-800 font-medium text-sm flex items-center gap-1"
                          title="Download E-Ticket"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => viewBookingDetails(bookingId)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {pagination.page} of {pagination.total_pages} ({pagination.total_bookings} total bookings)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleFilterChange('page', filters.page - 1)}
                disabled={!pagination.has_prev}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => handleFilterChange('page', filters.page + 1)}
                disabled={!pagination.has_next}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Booking Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Booking Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500">System Booking ID</p>
                  <p className="font-semibold text-xs break-all font-mono">{selectedBooking.booking_id || selectedBooking.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Supplier Booking ID</p>
                  <p className="font-semibold text-xs break-all font-mono">
                    {selectedBooking.supplier_booking_id
                      || selectedBooking.tripjack_booking_id
                      || selectedBooking.riya_pnr
                      || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">PNR</p>
                  <p className="font-semibold">{selectedBooking.pnr || selectedBooking.flight_data?.pnr || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Route</p>
                  <p className="font-semibold">
                    {(() => {
                      const f = selectedBooking.flight_data || {};
                      const r = selectedBooking.return_flight || {};
                      const isRt = !!(r.flightNumber || r.from || r.to)
                        || (selectedBooking.trip_type === 'R')
                        || (selectedBooking.riya_legs?.length > 1);
                      const onward = (f.from && f.to) ? `${f.from} → ${f.to}` : (selectedBooking.route || '-');
                      if (isRt) {
                        const ret = (r.from && r.to) ? `${r.from} → ${r.to}` : '↺';
                        return `${onward}  /  ${ret}`;
                      }
                      return onward;
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Travel Date</p>
                  <p className="font-semibold">
                    {(() => {
                      const td = selectedBooking.travel_date
                        || selectedBooking.flight_data?.flightDate
                        || selectedBooking.flight_data?.segments?.[0]?.dt;
                      return td ? new Date(td).toLocaleDateString() : '-';
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source</p>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    (selectedBooking.source || selectedBooking.flight_data?.source) === 'tripjack' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {(selectedBooking.source || selectedBooking.flight_data?.source || 'offline').toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Booking Type</p>
                  <p className="font-semibold">{selectedBooking.booking_type || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Airline / Flight</p>
                  <p className="font-semibold">
                    {selectedBooking.flight_data?.airline || '-'}
                    {selectedBooking.flight_data?.flightNumber ? ` • ${selectedBooking.flight_data.flightNumber}` : ''}
                    {selectedBooking.return_flight?.flightNumber && (
                      <>
                        <br />
                        <span className="text-xs text-gray-500">Return: </span>
                        {selectedBooking.return_flight?.airline || ''}
                        {selectedBooking.return_flight?.flightNumber ? ` • ${selectedBooking.return_flight.flightNumber}` : ''}
                      </>
                    )}
                  </p>
                </div>
                {(selectedBooking.return_riya_pnr && selectedBooking.return_riya_pnr !== selectedBooking.riya_pnr) && (
                  <div data-testid="admin-return-pnr">
                    <p className="text-sm text-gray-500">Return PNR</p>
                    <p className="font-semibold text-xs break-all font-mono">{selectedBooking.return_riya_pnr}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedBooking.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="font-semibold text-lg">₹{selectedBooking.total_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <p className="font-semibold">{selectedBooking.payment_mode || selectedBooking.payment_method || '-'}</p>
                </div>
              </div>

              {/* Flight Segments (for multi-segment flights) */}
              {selectedBooking.flight_data?.segments?.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {selectedBooking.return_flight ? 'Onward Flight Segments' : 'Flight Segments'}
                  </h3>
                  <div className="space-y-2">
                    {selectedBooking.flight_data.segments.map((seg, idx) => {
                      const daCode = seg.da?.code || seg.from?.code || '-';
                      const aaCode = seg.aa?.code || seg.to?.code || '-';
                      const daCity = seg.da?.city || seg.from?.city || '';
                      const aaCity = seg.aa?.city || seg.to?.city || '';
                      const daName = seg.da?.name || '';
                      const aaName = seg.aa?.name || '';
                      return (
                        <div key={idx} className="bg-gray-50 p-3 rounded-lg text-sm">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold">{daCode} → {aaCode}</span>
                                <span className="text-gray-600">
                                  {seg.fD?.aI?.name || seg.airline || ''}
                                  {seg.fD?.fN ? ` ${seg.fD.fN}` : ''}
                                </span>
                              </div>
                              {(daCity || daName || aaCity || aaName) && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {daCity}{daCity && daName ? ' • ' : ''}{daName}
                                  <span className="mx-2">→</span>
                                  {aaCity}{aaCity && aaName ? ' • ' : ''}{aaName}
                                </div>
                              )}
                            </div>
                            <div className="text-gray-600 text-xs whitespace-nowrap">
                              {seg.dt ? new Date(seg.dt).toLocaleString() : ''}
                              {seg.at ? <><br />→ {new Date(seg.at).toLocaleString()}</> : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Return Flight Segments — for round-trip bookings */}
              {selectedBooking.return_flight && (selectedBooking.return_flight.segments?.length > 0
                || selectedBooking.return_flight_data?.segments?.length > 0
                || selectedBooking.return_flight.flightNumber) && (
                <div className="mb-6" data-testid="admin-return-segments">
                  <h3 className="font-semibold text-gray-900 mb-3">Return Flight Segments</h3>
                  <div className="space-y-2">
                    {(selectedBooking.return_flight.segments?.length > 0
                        ? selectedBooking.return_flight.segments
                        : selectedBooking.return_flight_data?.segments?.length > 0
                        ? selectedBooking.return_flight_data.segments
                        : [{
                            da: { code: selectedBooking.return_flight.from },
                            aa: { code: selectedBooking.return_flight.to },
                            fD: { aI: { name: selectedBooking.return_flight.airline }, fN: selectedBooking.return_flight.flightNumber },
                            dt: selectedBooking.return_flight.departureTime,
                            at: selectedBooking.return_flight.arrivalTime,
                          }]
                    ).map((seg, idx) => {
                      const daCode = seg.da?.code || seg.from?.code || '-';
                      const aaCode = seg.aa?.code || seg.to?.code || '-';
                      const daCity = seg.da?.city || seg.from?.city || '';
                      const aaCity = seg.aa?.city || seg.to?.city || '';
                      const daName = seg.da?.name || '';
                      const aaName = seg.aa?.name || '';
                      return (
                        <div key={idx} className="bg-emerald-50 p-3 rounded-lg text-sm">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold">{daCode} → {aaCode}</span>
                                <span className="text-gray-600">
                                  {seg.fD?.aI?.name || seg.airline || ''}
                                  {seg.fD?.fN ? ` ${seg.fD.fN}` : ''}
                                </span>
                              </div>
                              {(daCity || daName || aaCity || aaName) && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {daCity}{daCity && daName ? ' • ' : ''}{daName}
                                  <span className="mx-2">→</span>
                                  {aaCity}{aaCity && aaName ? ' • ' : ''}{aaName}
                                </div>
                              )}
                            </div>
                            <div className="text-gray-600 text-xs whitespace-nowrap">
                              {seg.dt ? new Date(seg.dt).toLocaleString() : ''}
                              {seg.at ? <><br />→ {new Date(seg.at).toLocaleString()}</> : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Passengers */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Passengers ({selectedBooking.passengers?.length || 0})
                </h3>
                <div className="space-y-2">
                  {selectedBooking.passengers?.length > 0 ? selectedBooking.passengers.map((p, index) => {
                    const title = p.title || p.ti || '';
                    const firstName = p.first_name || p.firstName || p.fN || '';
                    const lastName = p.last_name || p.lastName || p.lN || '';
                    const ptype = p.type || p.pt || '';
                    const gender = p.gender || '';
                    const dob = p.dob || p.date_of_birth || p.dateOfBirth;
                    const passport = p.passport_number || p.passportNumber || p.pNum;
                    return (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <p className="font-medium">{title} {firstName} {lastName}</p>
                        <p className="text-sm text-gray-600">
                          {ptype}{gender ? ` • ${gender}` : ''}{dob ? ` • DOB: ${dob}` : ''}{passport ? ` • Passport: ${passport}` : ''}
                        </p>
                      </div>
                    );
                  }) : <p className="text-sm text-gray-500">No passenger data</p>}
                </div>
              </div>

              {/* Add-ons & Extras (Tripjack + Riya) — leg-grouped baggage,
                  per-segment meals/seats (industry standard). */}
              {((selectedBooking.ssr_breakdown && selectedBooking.ssr_breakdown.length > 0)
                || (Array.isArray(selectedBooking.leg_baggage_summary) && selectedBooking.leg_baggage_summary.length > 0)
                || Number(selectedBooking.ssr_amount || 0) > 0) && (
                <div className="mb-6" data-testid="admin-booking-addons-section">
                  {(() => {
                    const legBag = getLegBaggageRowsForBooking(selectedBooking);
                    const nonBag = getNonBaggageSsrRowsForBooking(selectedBooking);
                    const total =
                      legBag.reduce((s, r) => s + Number(r.total_amount || 0), 0)
                      + nonBag.reduce((s, r) => s + Number(r.amount || 0), 0);
                    const itemCount = legBag.length + nonBag.length;
                    return (
                      <>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          Add-ons & Extras
                          <span className="text-xs font-normal text-gray-500">
                            ({itemCount} item{itemCount === 1 ? '' : 's'} • ₹{Number(total || selectedBooking.ssr_amount || 0).toLocaleString()})
                          </span>
                        </h3>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                          {legBag.map((r, idx) => {
                            const legLabel = r.leg_label === 'return' ? 'Return' : 'Onward';
                            const route = r.leg_route ? ` · ${r.leg_route}` : '';
                            const paxLabel = r.pax_name || `Pax ${r.pax_ref || '?'}`;
                            return (
                              <div key={`bag-${idx}`} className="flex justify-between items-start gap-3 text-sm" data-testid={`admin-addon-bag-${idx}`}>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    <span className="mr-1">🧳</span>
                                    {r.description || r.code}
                                    {r.segment_count > 1 ? (
                                      <span className="ml-2 text-[10px] font-semibold text-gray-500">
                                        · spans {r.segment_count} segs
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-0.5">
                                    {legLabel}{route} • {paxLabel}
                                  </div>
                                </div>
                                <div className="font-semibold text-gray-900 whitespace-nowrap">
                                  ₹{Number(r.total_amount || 0).toLocaleString()}
                                </div>
                              </div>
                            );
                          })}
                          {nonBag.map((row, idx) => {
                            const icon = row.kind === 'meals' ? '🍽️' : row.kind === 'seats' ? '💺' : '✨';
                            const legLabel = row.leg === 'return' ? 'Return' : 'Onward';
                            const paxLabel = row.pax_name || `Pax ${row.pax_index || row.pax_ref || '?'}`;
                            return (
                              <div key={`nb-${idx}`} className="flex justify-between items-start gap-3 text-sm" data-testid={`admin-addon-nb-${idx}`}>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    <span className="mr-1">{icon}</span>
                                    {row.description || row.code}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-0.5">
                                    {legLabel} • {paxLabel}{row.segment_id ? ` • ${row.segment_id}` : ''}
                                  </div>
                                </div>
                                <div className="font-semibold text-gray-900 whitespace-nowrap">
                                  ₹{Number(row.amount || 0).toLocaleString()}
                                </div>
                              </div>
                            );
                          })}
                          {selectedBooking.seat_warning && (
                            <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-900 italic">
                              ⚠️ {selectedBooking.seat_warning}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Contact Details */}
              {(selectedBooking.contact_email || selectedBooking.contact_phone) && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Contact Information</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {selectedBooking.contact_email && (
                      <p><span className="text-gray-600">Email:</span> {selectedBooking.contact_email}</p>
                    )}
                    {selectedBooking.contact_phone && (
                      <p><span className="text-gray-600">Phone:</span> {selectedBooking.contact_phone}</p>
                    )}
                  </div>
                </div>
              )}

              {/* User Details */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">User Details</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p>
                    <span className="text-gray-600">Name:</span>{' '}
                    {selectedBooking.user_details?.name
                      || selectedBooking.user_details?.full_name
                      || selectedBooking.user_details?.agency_name
                      || selectedBooking.agent_name
                      || selectedBooking.agency_name
                      || '-'}
                  </p>
                  {selectedBooking.user_details?.agency_name && selectedBooking.user_details?.full_name && (
                    <p><span className="text-gray-600">Agency:</span> {selectedBooking.user_details.agency_name}</p>
                  )}
                  <p><span className="text-gray-600">Email:</span> {selectedBooking.user_details?.email || '-'}</p>
                  <p><span className="text-gray-600">Phone:</span> {selectedBooking.user_details?.phone || '-'}</p>
                  <p><span className="text-gray-600">Type:</span> {selectedBooking.user_type === 'b2c' ? 'B2C' : 'B2B (Agent)'}</p>
                </div>
              </div>

              {/* Sprint T2 — Order Note from Tripjack v2.0.1 */}
              {selectedBooking.orderNote && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-blue-800 uppercase mb-1">Order Note (from airline)</p>
                  <p className="text-sm text-blue-900">{selectedBooking.orderNote}</p>
                </div>
              )}

              {/* Cross-module ops links */}
              <div className="flex flex-wrap gap-2 text-xs mb-2">
                <a
                  href={`/admin/ops-monitor?focus=${encodeURIComponent(selectedBooking.booking_id || selectedBooking.id)}`}
                  className="px-2 py-1 rounded border border-[#1B375B] text-[#1B375B] font-semibold hover:bg-gray-50"
                >
                  Live Monitor
                </a>
                <a href="/admin/refunds" className="px-2 py-1 rounded border border-[#1B375B] text-[#1B375B] font-semibold hover:bg-gray-50">
                  Refund Tracker
                </a>
              </div>

              {/* Phase C1 — Payment & Refund (auto-hides cancellation block until cancelled) */}
              <PaymentAndRefundCard booking={selectedBooking} testIdPrefix="paref-admin-flight" />

              {/* In-flight Tripjack cancellation — resume UX */}
              {tjAdminAmendmentBanner && selectedBooking.status !== 'cancelled' && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Tripjack cancellation in progress</p>
                    <p className="text-xs text-amber-800 mt-1">
                      Reference <span className="font-mono">{tjAdminAmendmentBanner.amendment_id}</span>
                      {tjAdminAmendmentBanner.amendment_status ? (
                        <span>{' · '}{tjAdminAmendmentBanner.amendment_status}</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">Resume live status here, or wait for automated sync in the background.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTjResumePollingId(tjAdminAmendmentBanner.amendment_id);
                      setTjCancelBookingId(selectedBooking.booking_id || selectedBooking.id);
                      setTjCancelOpen(true);
                    }}
                    className="shrink-0 px-4 py-2 bg-amber-700 text-white text-sm font-medium rounded-lg hover:bg-amber-800"
                    data-testid="admin-tj-resume-amendment-btn"
                  >
                    Resume status tracking
                  </button>
                </div>
              )}

              {/* Actions */}
              {selectedBooking.status !== 'cancelled' && (
                <div className="flex flex-wrap gap-4">
                  {(() => {
                    const src = (selectedBooking.source || selectedBooking.flight_data?.source || '').toLowerCase();
                    const isOffline = src !== 'tripjack' && src !== 'riya'
                      && !selectedBooking.tripjack_booking_id && !selectedBooking.riya_pnr;
                    if (!isOffline) return null;
                    return (
                      <button
                        onClick={() => cancelBooking(selectedBooking.booking_id || selectedBooking.id)}
                        className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                        data-testid="admin-cancel-booking-btn"
                      >
                        <XCircle className="w-4 h-4" />
                        Cancel (offline only)
                      </button>
                    );
                  })()}
                  {/* Release Hold PNR — Sprint T2. Show only for ON_HOLD Tripjack bookings. */}
                  {(['on_hold', 'hold', 'pending'].includes((selectedBooking.tripjack_status || selectedBooking.status || '').toLowerCase())
                    && (selectedBooking.source || '').toLowerCase() !== 'riya'
                    && !selectedBooking.riya_pnr
                    && (selectedBooking.tripjack_booking_id || selectedBooking.supplier_booking_id)) && (
                    <button
                      onClick={() => releaseHoldPnr(selectedBooking)}
                      className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                      data-testid="admin-release-pnr-btn"
                    >
                      <Clock className="w-4 h-4" />
                      Release Hold PNR
                    </button>
                  )}
                  {/* Sprint P5 — Manual Tripjack refresh (pending → poll supplier + stamp PNR/tickets) */}
                  {(((selectedBooking.source || selectedBooking.flight_data?.source || '').toLowerCase() === 'tripjack')
                    && (selectedBooking.tripjack_booking_id || selectedBooking.supplier_booking_id)) && (
                    <button
                      onClick={() => refreshTripjackBooking(selectedBooking)}
                      className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2"
                      data-testid="admin-tripjack-refresh-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh Tripjack Status
                    </button>
                  )}
                  {/* Sprint H1+H2 · Confirm Hold ticket */}
                  {['on_hold', 'hold', 'pending'].includes((selectedBooking.tripjack_status || selectedBooking.riya_status || selectedBooking.status || '').toLowerCase()) && (
                    <button
                      onClick={() => { setConfirmHoldBooking(selectedBooking); setConfirmHoldOpen(true); }}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                      data-testid="admin-confirm-hold-btn"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Confirm Hold Ticket
                    </button>
                  )}
                  {/* Sprint C — Reschedule (Riya, confirmed) */}
                  {(((selectedBooking.source || '').toLowerCase() === 'riya') || selectedBooking.riya_pnr)
                    && ['confirmed', 'ticketed', 'success'].includes((selectedBooking.status || '').toLowerCase()) && (
                    <button
                      onClick={() => setRiyaRescheduleBooking(selectedBooking)}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                      data-testid="admin-riya-reschedule-btn"
                    >
                      <Calendar className="w-4 h-4" />
                      Reschedule
                    </button>
                  )}
                  {/* Sprint C — Add Extras / SSR (Riya, confirmed) */}
                  {(((selectedBooking.source || '').toLowerCase() === 'riya') || selectedBooking.riya_pnr)
                    && ['confirmed', 'ticketed', 'success'].includes((selectedBooking.status || '').toLowerCase()) && (
                    <button
                      onClick={() => setRiyaSSRBooking(selectedBooking)}
                      className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 flex items-center gap-2"
                      data-testid="admin-riya-ssr-btn"
                    >
                      <Plane className="w-4 h-4" />
                      Add Extras
                    </button>
                  )}
                </div>
              )}

              {selectedBooking.status === 'cancelled' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 font-medium">This booking has been cancelled</p>
                  {selectedBooking.cancellation_reason && (
                    <p className="text-red-700 text-sm mt-1">Reason: {selectedBooking.cancellation_reason}</p>
                  )}
                  {selectedBooking.refund_amount > 0 && (
                    <p className="text-red-700 text-sm mt-1">Refunded: ₹{selectedBooking.refund_amount}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sprint T1 — Tripjack cancel suite */}
      <TripjackCancelModal
        open={tjCancelOpen}
        onClose={() => {
          setTjCancelOpen(false);
          setTjResumePollingId(null);
        }}
        bookingId={tjCancelBookingId}
        resumePollingAmendmentId={tjResumePollingId}
        onCancelled={() => {
          setTjCancelOpen(false);
          setTjResumePollingId(null);
          setTjAdminAmendmentBanner(null);
          setShowDetailsModal(false);
          fetchBookings();
        }}
      />
      {/* Sprint A — Riya cancel + support fallback */}
      <RiyaCancelModal
        open={riyaCancelOpen}
        onClose={() => setRiyaCancelOpen(false)}
        bookingId={riyaCancelBooking?.id || riyaCancelBooking?.booking_id}
        booking={riyaCancelBooking || {}}
        mode={riyaCancelMode}
        onCancelled={() => {
          setRiyaCancelOpen(false);
          setShowDetailsModal(false);
          fetchBookings();
        }}
      />
      {/* Sprint C — Riya reschedule preview + SSR upsell */}
      <RiyaRescheduleModal
        open={!!riyaRescheduleBooking}
        onClose={() => setRiyaRescheduleBooking(null)}
        booking={riyaRescheduleBooking || {}}
      />
      <RiyaSSRModal
        open={!!riyaSSRBooking}
        onClose={() => setRiyaSSRBooking(null)}
        booking={riyaSSRBooking || {}}
      />
      {/* Sprint H1+H2 — Confirm Hold ticket */}
      <ConfirmHoldModal
        open={confirmHoldOpen}
        onClose={() => setConfirmHoldOpen(false)}
        booking={confirmHoldBooking || {}}
        onConfirmed={() => { setConfirmHoldOpen(false); setShowDetailsModal(false); fetchBookings(); }}
      />
    </div>
  );
};

export default BookingsManagement;
